import React, { useState, useEffect } from 'react';
import api from '../api';
import { FilePdf, FileXls, Funnel, CheckCircle, Clock, XCircle, Eye, Image } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const ReportsView = ({ clientId }) => {
  const [relatorios, setRelatorios] = useState([]);
  const [clients, setClients] = useState([]);
  const [prestadores, setPrestadores] = useState([]);
  const [filterClientId, setFilterClientId] = useState(clientId || '_all');
  const [filterStatus, setFilterStatus] = useState('_all');
  const [loading, setLoading] = useState(true);
  const [detailReport, setDetailReport] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [fotoExpandida, setFotoExpandida] = useState(null);

  useEffect(() => { fetchData(); }, [filterClientId, filterStatus]);

  const fetchData = async () => {
    try {
      const params = {};
      if (filterClientId && filterClientId !== '_all') params.client_id = filterClientId;
      if (filterStatus && filterStatus !== '_all') params.status = filterStatus;
      const [relRes, clientsRes, prestRes] = await Promise.all([
        api.get('/relatorios-atendimento', { params }),
        api.get('/clients'),
        api.get('/prestadores')
      ]);
      setRelatorios(relRes.data);
      setClients(clientsRes.data);
      setPrestadores(prestRes.data);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const getStatusColor = (status) => ({ 'pendente': 'bg-yellow-500 text-black', 'atribuida': 'bg-blue-500', 'em_andamento': 'bg-purple-500', 'aguardando_aprovacao': 'bg-orange-500', 'concluida': 'bg-emerald-600', 'cancelada': 'bg-red-500' })[status] || 'bg-zinc-500';
  const getStatusLabel = (status) => ({ 'pendente': 'Pendente', 'atribuida': 'Atribuida', 'em_andamento': 'Em Andamento', 'aguardando_aprovacao': 'Aguardando Aprovacao', 'concluida': 'Concluida', 'cancelada': 'Cancelada' })[status] || status;
  const getAprovacaoIcon = (status) => status === 'aprovado' ? <CheckCircle size={16} className="text-emerald-500" /> : status === 'rejeitado' ? <XCircle size={16} className="text-red-500" /> : <Clock size={16} className="text-yellow-500" />;

  const openDetail = (sol) => {
    setDetailReport(sol);
    setIsDetailOpen(true);
  };

  const generateDetailPDF = async (sol) => {
    try {
      const { default: jsPDF } = await import('jspdf');
      const doc = new jsPDF();

      // Load and add Toolserv logo
      try {
        const logoImg = await loadImageAsBase64(window.location.origin + '/toolserv-logo.png');
        if (logoImg) {
          doc.setFillColor(5, 5, 5);
          doc.rect(0, 0, 210, 30, 'F');
          doc.addImage(logoImg, 'PNG', 15, 5, 50, 20);
        }
      } catch { 
        // fallback: text logo
        doc.setFillColor(5, 5, 5);
        doc.rect(0, 0, 210, 30, 'F');
        doc.setFontSize(16);
        doc.setTextColor(255, 184, 0);
        doc.text('TOOLSERV', 15, 20);
      }

      doc.setFontSize(10);
      doc.setTextColor(255, 184, 0);
      doc.text('TOOLSERV - RELATORIO DE ATENDIMENTO', 15, 38);

      let y = 48;
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(9);

      // Report header
      doc.setFont(undefined, 'bold');
      doc.text(`Solicitacao: ${sol.numero_solicitacao}`, 15, y); y += 6;
      doc.setFont(undefined, 'normal');
      doc.text(`Cliente: ${sol.client_name}`, 15, y); y += 5;
      doc.text(`Status: ${getStatusLabel(sol.status)}`, 15, y); y += 5;
      doc.text(`Urgencia: ${sol.urgencia}`, 15, y); y += 5;
      doc.text(`Data Criacao: ${new Date(sol.created_at).toLocaleString('pt-BR')}`, 15, y); y += 8;

      // Services requested
      doc.setFont(undefined, 'bold');
      doc.text('SERVICOS SOLICITADOS:', 15, y); y += 5;
      doc.setFont(undefined, 'normal');
      sol.servicos_solicitados?.forEach(s => {
        if (y > 275) { doc.addPage(); y = 20; }
        doc.text(`  - ${s}`, 15, y); y += 4;
      });
      y += 4;

      if (sol.relatorio) {
        const rel = sol.relatorio;

        // Separator
        doc.setDrawColor(255, 184, 0);
        doc.line(15, y, 195, y); y += 6;

        doc.setFont(undefined, 'bold');
        doc.text('RELATORIO DO PRESTADOR', 15, y); y += 6;
        doc.setFont(undefined, 'normal');

        if (rel.eletricista_nome) {
          doc.text(`Eletricista: ${rel.eletricista_nome}`, 15, y); y += 5;
        }
        if (rel.auxiliar_nome) {
          doc.text(`Auxiliar: ${rel.auxiliar_nome}`, 15, y); y += 5;
        }
        doc.text(`Prestador: ${sol.prestador_nome || 'N/A'}`, 15, y); y += 5;

        if (rel.data_atendimento) {
          doc.text(`Data do Atendimento: ${new Date(rel.data_atendimento + 'T00:00:00').toLocaleDateString('pt-BR')}`, 15, y); y += 5;
        }

        if (rel.horario_chegada) {
          const chegada = new Date(rel.horario_chegada).toLocaleTimeString('pt-BR', {hour:'2-digit',minute:'2-digit'});
          const saida = new Date(rel.horario_saida).toLocaleTimeString('pt-BR', {hour:'2-digit',minute:'2-digit'});
          doc.text(`Horario: ${chegada} - ${saida}`, 15, y); y += 5;
        }

        doc.text(`Horas Trabalhadas: ${rel.horas_trabalhadas?.toFixed(1) || 0}h`, 15, y); y += 5;
        if (rel.horas_eletricista > 0) {
          doc.text(`  Eletricista: ${rel.horas_eletricista?.toFixed(1)}h | Auxiliar: ${(rel.horas_auxiliar || 0)?.toFixed(1)}h`, 15, y); y += 5;
        }
        y += 3;

        // Services executed
        if (rel.servicos_executados?.length > 0) {
          doc.setFont(undefined, 'bold');
          doc.text('SERVICOS EXECUTADOS:', 15, y); y += 5;
          doc.setFont(undefined, 'normal');
          rel.servicos_executados.forEach(s => {
            if (y > 275) { doc.addPage(); y = 20; }
            doc.text(`  - ${s.servico || s.categoria || JSON.stringify(s)}`, 15, y); y += 4;
          });
          y += 3;
        }

        if (rel.observacoes) {
          doc.setFont(undefined, 'bold');
          doc.text('OBSERVACOES:', 15, y); y += 5;
          doc.setFont(undefined, 'normal');
          const lines = doc.splitTextToSize(rel.observacoes, 175);
          lines.forEach(line => {
            if (y > 275) { doc.addPage(); y = 20; }
            doc.text(line, 15, y); y += 4;
          });
          y += 3;
        }

        // Photos
        if (rel.fotos_depois?.length > 0) {
          doc.setFont(undefined, 'bold');
          doc.text(`FOTOS DO SERVICO (${rel.fotos_depois.length} foto(s)):`, 15, y); y += 5;
          doc.setFont(undefined, 'normal');

          for (const fotoUrl of rel.fotos_depois) {
            try {
              if (y > 200) { doc.addPage(); y = 20; }
              const imgData = await loadImageAsBase64(fotoUrl);
              if (imgData) {
                doc.addImage(imgData, 'JPEG', 15, y, 80, 60);
                y += 65;
              } else {
                doc.text(`[Foto nao carregada]`, 15, y); y += 5;
              }
            } catch {
              doc.text(`[Foto nao carregada]`, 15, y); y += 5;
            }
          }
          y += 3;
        }

        // Approval info
        doc.setDrawColor(255, 184, 0);
        if (y > 260) { doc.addPage(); y = 20; }
        doc.line(15, y, 195, y); y += 6;
        doc.setFont(undefined, 'bold');
        doc.text('APROVACAO', 15, y); y += 5;
        doc.setFont(undefined, 'normal');
        doc.text(`Status: ${rel.status_aprovacao || 'pendente'}`, 15, y); y += 5;
        if (rel.aprovado_por) {
          doc.text(`Aprovado por: ${rel.aprovado_por}`, 15, y); y += 5;
        }
        if (rel.data_aprovacao) {
          doc.text(`Data/Hora: ${new Date(rel.data_aprovacao).toLocaleString('pt-BR')}`, 15, y); y += 5;
        }
        if (rel.feedback_cliente) {
          doc.text(`Feedback: ${rel.feedback_cliente}`, 15, y); y += 5;
        }
      }

      // Footer
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setTextColor(150, 150, 150);
        doc.text(`Toolserv - Gerado em ${new Date().toLocaleString('pt-BR')} - Pagina ${i}/${pageCount}`, 15, 290);
      }

      doc.save(`relatorio-${sol.numero_solicitacao}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Erro ao gerar PDF');
    }
  };

  const loadImageAsBase64 = (url) => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/jpeg', 0.7));
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
  };

  const exportToExcel = async () => {
    try {
      const XLSX = await import('xlsx');
      const data = relatorios.map(sol => ({
        'Numero': sol.numero_solicitacao, 'Cliente': sol.client_name,
        'Prestador': sol.prestador_nome || 'Nao atribuido', 'Status': getStatusLabel(sol.status),
        'Urgencia': sol.urgencia, 'Servicos': sol.servicos_solicitados?.join(', ') || '',
        'Horas': sol.horas_executadas?.toFixed(1) || '0', 'Aprovacao': sol.relatorio?.status_aprovacao || '',
        'Data': new Date(sol.created_at).toLocaleDateString('pt-BR')
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Relatorios');
      XLSX.writeFile(wb, 'relatorios-toolserv.xlsx');
    } catch (error) { console.error('Error:', error); }
  };

  if (loading) return <div className="text-center py-8 text-zinc-400">Carregando...</div>;

  const totalConcluidas = relatorios.filter(r => r.status === 'concluida').length;
  const totalHoras = relatorios.reduce((acc, r) => acc + (r.horas_executadas || 0), 0);
  const totalAguardando = relatorios.filter(r => r.status === 'aguardando_aprovacao').length;

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-[#121212] border border-[#27272A] rounded-sm p-4" data-testid="stat-concluidas">
          <div className="text-zinc-400 text-sm">Concluidas</div>
          <div className="text-3xl font-bold text-emerald-500" style={{ fontFamily: 'Barlow Condensed' }}>{totalConcluidas}</div>
        </div>
        <div className="bg-[#121212] border border-[#27272A] rounded-sm p-4" data-testid="stat-horas-total">
          <div className="text-zinc-400 text-sm">Total de Horas</div>
          <div className="text-3xl font-bold text-[#FFB800]" style={{ fontFamily: 'Barlow Condensed' }}>{totalHoras.toFixed(1)}h</div>
        </div>
        <div className="bg-[#121212] border border-[#27272A] rounded-sm p-4" data-testid="stat-aguardando">
          <div className="text-zinc-400 text-sm">Aguardando Aprovacao</div>
          <div className="text-3xl font-bold text-orange-500" style={{ fontFamily: 'Barlow Condensed' }}>{totalAguardando}</div>
        </div>
      </div>

      {/* Filters */}
      {!clientId && (
        <div className="bg-[#121212] border border-[#27272A] rounded-sm p-4 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Funnel size={20} className="text-[#FFB800]" />
            <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Barlow Condensed' }}>Filtros</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Cliente</Label>
              <Select value={filterClientId} onValueChange={setFilterClientId}>
                <SelectTrigger className="bg-[#050505] border-[#27272A] text-white" data-testid="filter-client-select"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#27272A] text-white">
                  <SelectItem value="_all">Todos os clientes</SelectItem>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="bg-[#050505] border-[#27272A] text-white" data-testid="filter-status-select"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#27272A] text-white">
                  <SelectItem value="_all">Todos</SelectItem>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="atribuida">Atribuida</SelectItem>
                  <SelectItem value="em_andamento">Em Andamento</SelectItem>
                  <SelectItem value="aguardando_aprovacao">Aguardando Aprovacao</SelectItem>
                  <SelectItem value="concluida">Concluida</SelectItem>
                  <SelectItem value="cancelada">Cancelada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {/* Export Buttons */}
      <div className="flex gap-3 mb-6">
        <Button onClick={() => { if (relatorios.length > 0) generateDetailPDF(relatorios[0]); }} className="bg-[#1E1E1E] text-white hover:bg-[#27272A]" data-testid="export-pdf-button">
          <FilePdf size={20} className="mr-2" />Exportar PDF
        </Button>
        <Button onClick={exportToExcel} className="bg-[#1E1E1E] text-white hover:bg-[#27272A]" data-testid="export-excel-button">
          <FileXls size={20} className="mr-2" />Exportar Excel
        </Button>
      </div>

      {/* Reports List */}
      <div className="space-y-4">
        {relatorios.length === 0 ? (
          <div className="bg-[#121212] border border-[#27272A] rounded-sm p-8 text-center text-zinc-400" data-testid="no-reports-message">Nenhum relatorio encontrado</div>
        ) : (
          relatorios.map(sol => (
            <div key={sol.id} className="bg-[#121212] border border-[#27272A] rounded-sm p-6" data-testid={`report-${sol.numero_solicitacao}`}>
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="text-xl font-bold text-[#FFB800]" style={{ fontFamily: 'Barlow Condensed' }}>{sol.numero_solicitacao}</div>
                  <Badge className={getStatusColor(sol.status)}>{getStatusLabel(sol.status)}</Badge>
                  <Badge className={sol.urgencia === 'urgente' ? 'bg-red-500' : sol.urgencia === 'rapido' ? 'bg-orange-500' : sol.urgencia === 'programado' ? 'bg-blue-500' : 'bg-zinc-500'}>{sol.urgencia}</Badge>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-2xl font-black text-[#FFB800]" style={{ fontFamily: 'Barlow Condensed' }}>{sol.horas_executadas?.toFixed(1) || '0.0'}h</div>
                  </div>
                  {/* Action buttons */}
                  <div className="flex gap-2">
                    <Button onClick={() => openDetail(sol)} size="sm" className="bg-[#1E1E1E] hover:bg-[#27272A] text-white" data-testid={`view-report-${sol.numero_solicitacao}`}>
                      <Eye size={16} className="mr-1" />Visualizar
                    </Button>
                    {sol.relatorio && (
                      <Button onClick={() => generateDetailPDF(sol)} size="sm" className="bg-[#1E1E1E] hover:bg-[#27272A] text-white" data-testid={`pdf-report-${sol.numero_solicitacao}`}>
                        <FilePdf size={16} className="mr-1" />PDF
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div><div className="text-zinc-400 text-xs mb-1">Cliente</div><div className="text-white font-medium">{sol.client_name}</div></div>
                <div><div className="text-zinc-400 text-xs mb-1">Prestador</div><div className="text-white font-medium">{sol.prestador_nome || 'Nao atribuido'}</div></div>
                <div><div className="text-zinc-400 text-xs mb-1">Data</div><div className="text-white font-medium">{new Date(sol.created_at).toLocaleDateString('pt-BR')}</div></div>
              </div>

              <div className="mb-4">
                <div className="text-zinc-400 text-xs mb-2">Servicos</div>
                <div className="flex flex-wrap gap-2">
                  {sol.servicos_solicitados?.map((s, idx) => <span key={idx} className="text-xs bg-[#1E1E1E] px-2 py-1 rounded text-zinc-300">{s}</span>)}
                </div>
              </div>

              {sol.relatorio && (
                <div className="mt-3 p-3 bg-[#050505] border border-[#27272A] rounded-sm">
                  <div className="flex items-center gap-2 text-xs">
                    {getAprovacaoIcon(sol.relatorio.status_aprovacao)}
                    <span className="text-zinc-400">{sol.relatorio.status_aprovacao}</span>
                    <span className="text-zinc-600">|</span>
                    <span className="text-[#22C55E] font-bold">{sol.relatorio.horas_trabalhadas?.toFixed(1)}h</span>
                    {sol.relatorio.eletricista_nome && <span className="text-zinc-500">| Elet: {sol.relatorio.eletricista_nome}</span>}
                    {sol.relatorio.auxiliar_nome && <span className="text-zinc-500">| Aux: {sol.relatorio.auxiliar_nome}</span>}
                    {sol.relatorio.fotos_depois?.length > 0 && <span className="text-zinc-500">| {sol.relatorio.fotos_depois.length} foto(s)</span>}
                  </div>
                </div>
              )}

              <div className="mt-3 pt-3 border-t border-[#27272A] flex flex-wrap gap-4 text-xs text-zinc-500">
                <span>Criado: {new Date(sol.created_at).toLocaleString('pt-BR')}</span>
                {sol.data_conclusao && <span>Concluido: {new Date(sol.data_conclusao).toLocaleString('pt-BR')}</span>}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Detail Modal */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">Detalhes do Atendimento</DialogTitle></DialogHeader>
          {detailReport && (
            <div className="space-y-6">
              {/* Header */}
              <div className="p-4 bg-[#050505] border border-[#27272A] rounded-sm">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-2xl font-bold text-[#FFB800]" style={{ fontFamily: 'Barlow Condensed' }}>{detailReport.numero_solicitacao}</div>
                    <div className="text-white mt-1">{detailReport.client_name}</div>
                  </div>
                  <Badge className={getStatusColor(detailReport.status)}>{getStatusLabel(detailReport.status)}</Badge>
                </div>
              </div>

              {/* Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div><div className="text-zinc-500 text-xs">Prestador</div><div className="text-white text-sm">{detailReport.prestador_nome || 'N/A'}</div></div>
                <div><div className="text-zinc-500 text-xs">Urgencia</div><div className="text-white text-sm capitalize">{detailReport.urgencia}</div></div>
                <div><div className="text-zinc-500 text-xs">Data Solicitacao</div><div className="text-white text-sm">{new Date(detailReport.created_at).toLocaleDateString('pt-BR')}</div></div>
                <div><div className="text-zinc-500 text-xs">Horas Executadas</div><div className="text-[#FFB800] font-bold">{detailReport.horas_executadas?.toFixed(1) || 0}h</div></div>
              </div>

              {/* Services */}
              <div>
                <div className="text-zinc-500 text-xs mb-2">Servicos Solicitados</div>
                <div className="flex flex-wrap gap-2">
                  {detailReport.servicos_solicitados?.map((s, i) => <span key={i} className="text-xs bg-[#1E1E1E] px-2 py-1 rounded text-zinc-300 border border-[#27272A]">{s}</span>)}
                </div>
              </div>

              {/* Fotos do Problema */}
              {detailReport.fotos_problema?.length > 0 && (
                <div>
                  <div className="text-zinc-500 text-xs mb-2">Fotos do Problema (enviadas pelo cliente)</div>
                  <div className="grid grid-cols-4 gap-2">
                    {detailReport.fotos_problema.map((url, i) => (
                      <img key={i} src={url} alt={`Problema ${i+1}`} className="w-full h-24 object-cover rounded-sm border border-[#27272A] cursor-pointer hover:border-[#FFB800]" onClick={() => setFotoExpandida(url)} />
                    ))}
                  </div>
                </div>
              )}

              {/* Report details */}
              {detailReport.relatorio && (
                <div className="border border-[#27272A] rounded-sm overflow-hidden">
                  <div className="bg-[#FFB800]/10 border-b border-[#27272A] px-4 py-2">
                    <h4 className="text-[#FFB800] font-semibold text-sm">Relatorio do Tecnico</h4>
                  </div>
                  <div className="p-4 space-y-4">
                    {/* Workers */}
                    <div className="grid grid-cols-2 gap-4">
                      {detailReport.relatorio.eletricista_nome && (
                        <div><div className="text-zinc-500 text-xs">Eletricista</div><div className="text-white font-medium">{detailReport.relatorio.eletricista_nome}</div></div>
                      )}
                      {detailReport.relatorio.auxiliar_nome && (
                        <div><div className="text-zinc-500 text-xs">Auxiliar</div><div className="text-white font-medium">{detailReport.relatorio.auxiliar_nome}</div></div>
                      )}
                    </div>

                    {/* Times */}
                    <div className="grid grid-cols-3 gap-4">
                      {detailReport.relatorio.data_atendimento && (
                        <div><div className="text-zinc-500 text-xs">Data Atendimento</div><div className="text-white text-sm">{new Date(detailReport.relatorio.data_atendimento + 'T00:00:00').toLocaleDateString('pt-BR')}</div></div>
                      )}
                      <div><div className="text-zinc-500 text-xs">Chegada</div><div className="text-white text-sm">{detailReport.relatorio.horario_chegada ? new Date(detailReport.relatorio.horario_chegada).toLocaleTimeString('pt-BR', {hour:'2-digit',minute:'2-digit'}) : '-'}</div></div>
                      <div><div className="text-zinc-500 text-xs">Saida</div><div className="text-white text-sm">{detailReport.relatorio.horario_saida ? new Date(detailReport.relatorio.horario_saida).toLocaleTimeString('pt-BR', {hour:'2-digit',minute:'2-digit'}) : '-'}</div></div>
                    </div>

                    {/* Hours */}
                    <div className="p-3 bg-[#050505] rounded-sm">
                      <div className="text-[#FFB800] font-bold text-lg">{detailReport.relatorio.horas_trabalhadas?.toFixed(1)}h trabalhadas</div>
                      {(detailReport.relatorio.horas_eletricista > 0 || detailReport.relatorio.horas_auxiliar > 0) && (
                        <div className="text-zinc-400 text-xs mt-1">
                          Eletricista: {detailReport.relatorio.horas_eletricista?.toFixed(1)}h
                          {detailReport.relatorio.horas_auxiliar > 0 && <span className="ml-3">Auxiliar: {detailReport.relatorio.horas_auxiliar?.toFixed(1)}h</span>}
                        </div>
                      )}
                    </div>

                    {/* Services executed */}
                    {detailReport.relatorio.servicos_executados?.length > 0 && (
                      <div>
                        <div className="text-zinc-500 text-xs mb-2">Servicos Executados</div>
                        <div className="flex flex-wrap gap-2">
                          {detailReport.relatorio.servicos_executados.map((s, i) => (
                            <span key={i} className="text-xs bg-[#050505] px-2 py-1 rounded text-emerald-400 border border-emerald-800">{s.servico || s.categoria || JSON.stringify(s)}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Observations */}
                    {detailReport.relatorio.observacoes && (
                      <div>
                        <div className="text-zinc-500 text-xs mb-1">Observacoes</div>
                        <div className="text-white text-sm bg-[#050505] p-3 rounded-sm border border-[#27272A]">{detailReport.relatorio.observacoes}</div>
                      </div>
                    )}

                    {/* Photos */}
                    {detailReport.relatorio.fotos_depois?.length > 0 && (
                      <div>
                        <div className="text-zinc-500 text-xs mb-2 flex items-center gap-1"><Image size={14} />Fotos do Servico ({detailReport.relatorio.fotos_depois.length})</div>
                        <div className="grid grid-cols-3 gap-3">
                          {detailReport.relatorio.fotos_depois.map((url, i) => (
                            <img key={i} src={url} alt={`Foto ${i+1}`} className="w-full h-32 object-cover rounded-sm border border-[#27272A] cursor-pointer hover:border-[#FFB800]" onClick={() => setFotoExpandida(url)} />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Approval */}
                    <div className="border-t border-[#27272A] pt-4 mt-4">
                      <div className="flex items-center gap-2 mb-2">
                        {getAprovacaoIcon(detailReport.relatorio.status_aprovacao)}
                        <span className="text-white font-medium text-sm">Aprovacao: {detailReport.relatorio.status_aprovacao}</span>
                      </div>
                      {detailReport.relatorio.aprovado_por && (
                        <div className="text-zinc-400 text-sm">Aprovado por: <span className="text-white">{detailReport.relatorio.aprovado_por}</span></div>
                      )}
                      {detailReport.relatorio.data_aprovacao && (
                        <div className="text-zinc-400 text-sm">Data: <span className="text-white">{new Date(detailReport.relatorio.data_aprovacao).toLocaleString('pt-BR')}</span></div>
                      )}
                      {detailReport.relatorio.feedback_cliente && (
                        <div className="text-zinc-400 text-sm mt-1">Feedback: <span className="text-white italic">"{detailReport.relatorio.feedback_cliente}"</span></div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* PDF Button */}
              {detailReport.relatorio && (
                <Button onClick={() => generateDetailPDF(detailReport)} className="w-full bg-[#FFB800] text-black hover:bg-[#E6A600]" data-testid="generate-pdf-detail">
                  <FilePdf size={20} className="mr-2" />Gerar PDF Detalhado
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Expanded Photo Modal */}
      <Dialog open={!!fotoExpandida} onOpenChange={() => setFotoExpandida(null)}>
        <DialogContent className="bg-[#121212] border-[#27272A] max-w-3xl p-2">
          {fotoExpandida && <img src={fotoExpandida} alt="Foto expandida" className="w-full max-h-[80vh] object-contain rounded-sm" />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportsView;
